import { getPrivateLabelId } from '@tcc/shared/src/helpers/page';
import { hasAnalyticsConsent, hasMarketingConsent } from '@tcc/shared/src/helpers/policy';
import { buildCorporateUrl } from '@tcc/shared/src/helpers/page';
import { map as objectMap, merge } from '@exp/exp-utils/helper/object';
import config from '@exp/exp-utils/helper/config';
import page from '@tcc/shared/src/interfaces/traffic/eventPageProperties';
import { getAccountId } from './helper';

/**
 * SGTM Controller should know the following:
 * - isEnabled
 *   - aka whether or not to activate the Gtag Controller. Determined by PLID
 * - Consent Mode
 *   - how to handle _initDataLayer and loading of gtag.js
 *   - how to update consent mode based off consent
 * - how to handle gtag config records
 */
/**
 * EDGE CASES:
 *   - if consent mode is ACTIVE: ensure default consent record and gtag is loaded (regardless of consent)
*/

const groupGa4 = 'groupGa4';

const getTransportUrl = () => {
  const sgtmHost = config.get('tcc.sGtmHost');
  return (sgtmHost && sgtmHost !== 'undefined') ? `https://g.sst.${sgtmHost}` : buildCorporateUrl('g.sst');
};

/**
 * @name isEnabled
 * @description Determines whether or not the event should be sent to SGTM
 * @returns {Boolean|undefined} where true means the event should be sent to SGTM
 */
const isEnabled = () => {
  // PLID mappings: https://secureservernet.sharepoint.com/sites/TechHub/SitePages/Private-label-brands.aspx
  const enabledPlids = [1, 587240];

  if (enabledPlids.includes(getPrivateLabelId())) {
    return true;
  }
};

const handleConsentMode = (type) => {
  const denied = 'denied';
  const granted = 'granted';
  const consentRecord = {
    ad_storage: denied,
    analytics_storage: denied
  };

  if (type === 'update') {
    consentRecord.ad_storage = hasMarketingConsent() ? granted : denied;
    consentRecord.analytics_storage = hasAnalyticsConsent() ? granted : denied;
  }

  return consentRecord;
};

/**
 * @name getSgtmConfigRecords
 * @description stores Sgtm config records to initialize. Currently only for GA4 (client side or server side)
 * @returns {Array} Containing Objects with id (tracker id) and config params property
 * @example
 * [
 *    // GA4 - server side w/ SGTM
 *    {  'G-XXXX1', {
 *          send_page_view: false,
 *          anonymize_ip: true,
 *          page_referrer: 'https://somereferrer'
 *          transport_url: 'https://sgtmserver.domain.com'
 *          groups: 'groupGa4'
 *       }
 *    },
 *    // GA4 - client side
 *    { 'G-XXXX2', {
 *          send_page_view: false,
 *          anonymize_ip: true,
 *          page_referrer: 'https://somereferrer'
 *          groups: 'groupGa4'
 *        }
 *    }
 *    // UA - server side w/ SGTM
 *    { 'UA-XXXXX', {
 *          send_page_view: false,
 *          anonymize_ip: true,
 *          page_referrer: 'https://somereferrer'
 *          transport_url: 'https://sgtmserver.domain.com'
 *        }
 *    }
 * ]
 */
const getSgtmConfigRecords = () => {
  return [{
    id: getAccountId('sGtm'),
    params: {
      send_page_view: false,
      anonymize_ip: true,
      page_referrer: page.get('referrer'),
      transport_url: getTransportUrl(),
      first_party_collection: true,
      groups: groupGa4
    }
  }];
};

/**
 * @description Transforms item object with GA4 Items Property
 * @link https://developers.google.com/analytics/devguides/collection/ga4/reference/events#purchase_item
 * @param {Object} itemObj item of Items Property
 * @returns {Object} returns transformed item object
 * @example
 * // Ecommerce Example
 * // input
 * const oldItemObj = {
 *  id: '123',
 *  brand: 'some brand',
 *  category: 'some category',
 *  name: 'some name',
 *  variant: 'some variant'
 * }
 * // output
 * const updatedItemObj = {
 *  item_id: '123',
 *  item_brand: 'some brand',
 *  item_category: 'some category',
 *  item_name: 'some name',
 *  item_variant: 'some variant'
 * }
 */
const _transformGa4ItemProp = (itemObj) => {
  const ga4ItemKey = {
    id: 'item_id',
    brand: 'item_brand',
    category: 'item_category',
    name: 'item_name',
    variant: 'item_variant'
  };
  const ga4ItemObj = {};
  objectMap(itemObj, (key, value) => {
    if (ga4ItemKey[key]) {
      ga4ItemObj[ga4ItemKey[key]] = value;
    } else {
      ga4ItemObj[key] = value;
    }
  });
  return ga4ItemObj;
};

const _transformEventProps = ({ eId, eventAction, eventData = {}, eventObj = {} }) => {
  const baseGa4EventObj = {
    visitguid: eventObj.visitguid_hit,
    visitorguid: eventObj.visitorguid_hit,
    logged_in_status: eventObj.logged_in_status_hit,
    customer_agent_bool: eventObj.customer_agent_bool_hit,
    delegated_bool: eventObj.delegated_bool_hit,
    market: eventObj.market_hit,
    referrer: eventObj.referrer_hit,
    user_agent: eventObj.user_agent_hit,
    querystring: eventObj.querystring_hit,
    privatelabelid: eventObj.privatelabelid_hit,
    timestamp: eventObj.timestamp_hit,
    google_client_id: eventObj.google_client_id_hit,
    is_gd_salessite_bool: eventObj.is_gd_salessite_bool_hit,
    correlation_id: eventObj.correlation_id_hit,
    traffic_load_source: eventObj.traffic_load_source_hit,
    page_id: eventObj.page_id_hit,
    traffic_tcc_version_hit: eventObj.traffic_tcc_version_hit,
    hit_id: eventObj.hit_id_hit,
    realm: eventObj.realm_hit,
    event_action: eventAction,
    event_category: eventObj.event_category,
    content_group: eventObj.content_group1,
    transport_type: eventObj.transport_type,
    analytics_consent: hasAnalyticsConsent(),
    marketing_consent: hasMarketingConsent(),
    // eventObj.event_action is pulling from EID field for ecomm/generic conversion events
    event_id: eId || eventObj.event_action,
    user_properties: {
      visitorguid: eventObj.visitorguid_user_scoped,
      google_client_id: eventObj.google_client_id_user_scoped,
      existing_shopper_user: eventObj.existing_shopper_user_scoped,
      shopper_id: eventObj.shopper_id_user_scoped,
      customer_id: eventObj.customer_id_user_scoped,
      customer_agent_bool: eventObj.customer_agent_bool_hit
    },
    send_to: groupGa4
  };

  if (eventObj.non_interaction)
    baseGa4EventObj.non_interaction = eventObj.non_interaction;

  // ecom events (excluding promotions)
  if (eventObj.items) {
    baseGa4EventObj.items = [];
    eventObj.items.forEach((item, index) => {
      baseGa4EventObj.items[index] = _transformGa4ItemProp(item);
    });
  }

  // ecom - promotions
  if (eventObj.promotions) {
    baseGa4EventObj.items = [];
    eventObj.promotions.forEach((item, index) => {
      baseGa4EventObj.items[index] = _transformGa4ItemProp(item);
    });
  }

  // page event - generic conversions
  if (eventData.properties) {
    baseGa4EventObj.package_category = eventData.properties.package_category;
    baseGa4EventObj.package_id = eventData.properties.package_id;
    baseGa4EventObj.transaction_id = eventData.properties.virtual_order_id;
  }

  return baseGa4EventObj;
};

/**
 * @name buildPageView
 * @description Builds Google Analytics 4 (GA4) page view event payloads
 * - Transforms event params
 * @param {Object} eventAction UA event action
 * @param {Object} eventObj event object (processed by filtermap)
 * @returns {Object} with event param property
 */
const buildPageView = (eventAction, eventObj) => {
  const ga4EventObj = _transformEventProps({ eventAction, eventObj });

  ga4EventObj.referrer = eventObj.referrer_hit;
  ga4EventObj.location_uri = eventObj.location_uri_hit;
  ga4EventObj.page_path = eventObj.page_path;
  ga4EventObj.send_page_view = eventObj.send_page_view;

  return {
    params: ga4EventObj
  };
};

/**
 * @description Builds generic conversion (aka 'tcc.conversion.*') and page event payloads.
 * This builder is invoked by ga.SendPageEvent
 * @param {String} eId event id
 * @param {Object} eventData event data object
 * @param {Object} eventObj event object (processed by filtermap)
 * @returns {Object} With GA4 event name and event param property
 */
const buildPageEvent = (eId, eventData, eventObj) => {
  const ga4EventObj = _transformEventProps({ eId, eventAction: eId, eventData, eventObj });
  return {
    name: 'generic_interaction',
    params: merge(eventData, ga4EventObj)
  };
};

/**
 * @name buildEcommerceEvent
 * @description Builds the payload for an Ecommerce Event
 * @param {String} eventAction UA Event action (ex. 'purchase', 'add_to_cart', etc);
 * @param {Object} eventData Event data object
 * @param {Object} eventObj Event Object (processed by filtermap)
 * @returns {Object} with GA4 event name and event param property
 */
const buildEcommerceEvent = (eventAction, eventData, eventObj) => {
  const ga4EventObj = _transformEventProps({
    eventAction: eventObj.event_action,
    eventData,
    eventObj });
  return {
    name: (eventAction === 'select_content' ? 'select_promotion' : eventAction),
    params: merge(eventData, ga4EventObj)
  };
};

/**
 * @name updateConsent
 * @description Update analytics and marketing consent from the consent cookie
 * @param {Object} eventData value
 */
const updateConsent = (eventData) => {
  if (eventData && eventData.hasOwnProperty('analytics_consent') && eventData.hasOwnProperty('marketing_consent')) {
    eventData.analytics_consent = hasAnalyticsConsent();
    eventData.marketing_consent = hasMarketingConsent();
  }
};

export {
  isEnabled,
  updateConsent,
  handleConsentMode,
  buildCorporateUrl,
  buildPageEvent,
  buildEcommerceEvent,
  buildPageView,
  getSgtmConfigRecords,
  getTransportUrl
};

// Private exports for testing
export {
  _transformEventProps,
  _transformGa4ItemProp
};
